import { useRef, useState, useEffect } from "react";
import { Minus, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import OathboundFrame from "@/components/OathboundFrame/OathboundFrame";
import PurchaseVerification from "@/components/PurchaseVerification/PurchaseVerification";
import SpecularButton from "@/components/SpecularButton/SpecularButton";
import {
  buildCreateOrderPayload,
  calculateCheckoutPricing,
  loadRazorpayCheckout,
  postPurchaseApi,
  storePurchaseOutcome,
  validatePurchaseDetails,
  validatePurchaseField,
} from "./purchaseCheckout";
import "./PurchasePage.css";

const MIN_QUANTITY = 1;
const MAX_QUANTITY = 10;
const UNIT_PRICE_PAISE = 69_900;
const SHIPPING_PAISE = 5_000;

const initialDetails = {
  customerName: "",
  customerEmail: "",
  customerPhone: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  postalCode: "",
  country: "India",
};

function fieldClass(errors, name, isWide = false) {
  return [
    "purchase-field",
    isWide ? "is-wide" : "",
    errors[name] ? "has-error" : "",
  ]
    .filter(Boolean)
    .join(" ");
}

function FieldError({ errors, name }) {
  const message = errors[name];

  return (
    <span
      className={`purchase-field-error${message ? "" : " is-placeholder"}`}
      id={`${name}-error`}
      aria-hidden={!message}
    >
      {message || "\u00a0"}
    </span>
  );
}

function formatPaise(amountPaise) {
  const hasPaise = amountPaise % 100 !== 0;
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: hasPaise ? 2 : 0,
    maximumFractionDigits: hasPaise ? 2 : 0,
  }).format(amountPaise / 100);
}

export default function PurchasePage() {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(
    window.matchMedia("(max-width: 767px)").matches,
  );
  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 767px)");
    const handleChange = (event) => setIsMobile(event.matches);
    mediaQuery.addEventListener?.("change", handleChange);
    return () => mediaQuery.removeEventListener?.("change", handleChange);
  }, []);
  const formRef = useRef(null);
  const checkoutLockRef = useRef(false);
  const [details, setDetails] = useState(initialDetails);
  const [quantity, setQuantity] = useState(MIN_QUANTITY);
  const [errors, setErrors] = useState({});
  const [checkoutPhase, setCheckoutPhase] = useState("idle");
  const [checkoutMessage, setCheckoutMessage] = useState("");
  const [discountInput, setDiscountInput] = useState("");
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [discountPhase, setDiscountPhase] = useState("idle");
  const [discountMessage, setDiscountMessage] = useState("");
  const pricing = calculateCheckoutPricing(
    quantity,
    appliedDiscount?.discountPerBookPaise ?? 0,
  );
  const {
    bookSubtotalPaise,
    discountTotalPaise,
    totalAmountPaise: totalPaise,
  } = pricing;
  const checkoutBusy = [
    "loading",
    "creating",
    "checkout",
    "verifying",
  ].includes(checkoutPhase);
  const discountBusy = discountPhase === "applying";

  const updateDetails = (event) => {
    const { name } = event.target;
    let { value } = event.target;

    // Phone and postal code inputs accept digits only before validation.
    if (name === "customerPhone") value = value.replace(/\D/g, "").slice(0, 10);
    if (name === "postalCode") value = value.replace(/\D/g, "").slice(0, 6);

    setDetails((current) => ({ ...current, [name]: value }));

    // Once a field has been flagged, keep its feedback current while editing.
    if (errors[name]) {
      setErrors((current) => {
        const next = { ...current };
        const message = validatePurchaseField(name, value);
        if (message) next[name] = message;
        else delete next[name];
        return next;
      });
    }
  };

  const validateOnBlur = (event) => {
    const { name, value } = event.target;
    const message = validatePurchaseField(name, value);
    setErrors((current) => {
      const next = { ...current };
      if (message) next[name] = message;
      else delete next[name];
      return next;
    });
  };

  const adjustQuantity = (change) => {
    setQuantity((current) =>
      Math.min(MAX_QUANTITY, Math.max(MIN_QUANTITY, current + change)),
    );
  };

  const updateDiscountInput = (event) => {
    const value = event.target.value
      .replace(/[^A-Za-z0-9]/g, "")
      .toUpperCase()
      .slice(0, 32);
    setDiscountInput(value);
    if (discountPhase === "error") {
      setDiscountPhase("idle");
      setDiscountMessage("");
    }
  };

  const removeDiscount = () => {
    setAppliedDiscount(null);
    setDiscountInput("");
    setDiscountPhase("idle");
    setDiscountMessage("");
  };

  const applyDiscount = async () => {
    if (discountInput.length < 3) {
      setDiscountPhase("error");
      setDiscountMessage("Enter a valid discount code.");
      return;
    }

    setDiscountPhase("applying");
    setDiscountMessage("Checking code…");

    try {
      const response = await postPurchaseApi("/api/apply-discount", {
        discountCode: discountInput,
        quantity,
      });
      if (!response?.success || !response.discount) {
        throw new Error("The discount code could not be applied.");
      }

      setAppliedDiscount(response.discount);
      setDiscountInput(response.discount.code);
      setDiscountPhase("applied");
      setDiscountMessage(
        `${response.discount.code} applied — ${formatPaise(
          response.discount.discountPerBookPaise,
        )} off each book.`,
      );
    } catch (error) {
      setAppliedDiscount(null);
      setDiscountPhase("error");
      setDiscountMessage(
        error instanceof Error
          ? error.message
          : "The discount code could not be applied.",
      );
    }
  };

  const handlePurchase = async (event) => {
    event.preventDefault();
    if (checkoutLockRef.current) return;
    setCheckoutMessage("");

    // Stop before any network call and focus the first invalid field.
    const validationErrors = validatePurchaseDetails(details);
    setErrors(validationErrors);
    const firstInvalidField = Object.keys(validationErrors)[0];
    if (firstInvalidField) {
      setCheckoutMessage("Please correct the highlighted fields.");
      window.requestAnimationFrame(() => {
        formRef.current?.elements.namedItem(firstInvalidField)?.focus();
      });
      return;
    }

    const razorpayKeyId = process.env.REACT_APP_RAZORPAY_KEY_ID?.trim();
    if (!razorpayKeyId) {
      setCheckoutMessage("Payment checkout is not configured yet.");
      return;
    }

    checkoutLockRef.current = true;

    try {
      setCheckoutPhase("loading");
      await loadRazorpayCheckout();

      setCheckoutPhase("creating");
      const order = await postPurchaseApi(
        "/api/create-order",
        buildCreateOrderPayload(
          details,
          quantity,
          appliedDiscount?.code,
        ),
      );

      let verificationStarted = false;
      let outcomeNavigated = false;
      const checkout = new window.Razorpay({
        key: razorpayKeyId,
        order_id: order.order_id,
        amount: order.amount,
        currency: order.currency,
        name: "The World of the Pehuans",
        description: `The Oath of the Unhuan × ${quantity}`,
        image: `${window.location.origin}/media/images/Oath_logo.avif`,
        prefill: {
          name: details.customerName.trim(),
          email: details.customerEmail.trim(),
          contact: `+91${details.customerPhone}`,
        },
        theme: {
          color: "#aa803b",
          backdrop_color: "#12110f",
        },
        modal: {
          confirm_close: true,
          ondismiss: () => {
            if (!verificationStarted) {
              checkoutLockRef.current = false;
              setCheckoutPhase("idle");
              setCheckoutMessage(
                "Checkout was closed. You can try again when ready.",
              );
            }
          },
        },
        retry: {
          enabled: true,
        },
        handler: async (payment) => {
          verificationStarted = true;
          setCheckoutPhase("verifying");
          setCheckoutMessage("Verifying your payment securely…");

          try {
            const verification = await postPurchaseApi("/api/verify-payment", {
              razorpay_payment_id: payment.razorpay_payment_id,
              razorpay_order_id: payment.razorpay_order_id,
              razorpay_signature: payment.razorpay_signature,
            });

            if (!verification?.success) {
              throw new Error("Payment verification was not successful.");
            }
            if (!verification.receipt) {
              throw new Error("The verified order receipt was not returned.");
            }

            const outcome = {
              status: "success",
              receipt: verification.receipt,
            };
            outcomeNavigated = true;
            storePurchaseOutcome(outcome);
            navigate("/purchase/receipt", { replace: true, state: outcome });
          } catch {
            const outcome = {
              status: "failure",
              failure: {
                kind: "verification",
                providerOrderId: payment.razorpay_order_id || order.order_id,
                paymentReference: payment.razorpay_payment_id,
              },
            };
            outcomeNavigated = true;
            storePurchaseOutcome(outcome);
            navigate("/purchase/receipt", { replace: true, state: outcome });
          }
        },
      });

      checkout.on("payment.failed", (response) => {
        if (outcomeNavigated) return;
        verificationStarted = true;
        outcomeNavigated = true;
        const outcome = {
          status: "failure",
          failure: {
            kind: "payment",
            providerOrderId:
              response?.error?.metadata?.order_id || order.order_id,
            paymentReference: response?.error?.metadata?.payment_id,
          },
        };
        storePurchaseOutcome(outcome);
        try {
          checkout.close?.();
        } catch {
          // Navigation still surfaces the failure if Checkout cannot close itself.
        }
        navigate("/purchase/receipt", { replace: true, state: outcome });
      });

      setCheckoutPhase("checkout");
      checkout.open();
    } catch (error) {
      checkoutLockRef.current = false;
      setCheckoutPhase("idle");
      if (
        typeof error?.code === "string" &&
        error.code.includes("DISCOUNT_CODE")
      ) {
        setAppliedDiscount(null);
        setDiscountPhase("error");
        setDiscountMessage(error.message);
      }
      setCheckoutMessage(
        error instanceof Error
          ? error.message
          : "Checkout could not be opened. Please try again.",
      );
    }
  };

  if (checkoutPhase === "verifying") {
    return <PurchaseVerification />;
  }

  return (
    <main className="purchase-page" aria-labelledby="purchase-title">
      <OathboundFrame />

      <section className="purchase-content">
        <div className="purchase-heading">
          {isMobile ? (
            <></>
          ) : (
            <p className="purchase-eyebrow">Premium hardcover edition</p>
          )}
          <h1 id="purchase-title">Where should we send your book?</h1>
          <p>Enter your contact and delivery details below.</p>
        </div>

        <form
          ref={formRef}
          className="purchase-card"
          onSubmit={handlePurchase}
          noValidate
        >
          <div className="purchase-fields">
            <fieldset>
              <legend>Contact details</legend>
              <div className="purchase-field-grid">
                <label className={fieldClass(errors, "customerName", true)}>
                  <span>Full name</span>
                  <input
                    type="text"
                    name="customerName"
                    value={details.customerName}
                    onChange={updateDetails}
                    onBlur={validateOnBlur}
                    autoComplete="name"
                    maxLength="120"
                    aria-invalid={Boolean(errors.customerName)}
                    aria-describedby={
                      errors.customerName ? "customerName-error" : undefined
                    }
                    required
                  />
                  <FieldError errors={errors} name="customerName" />
                </label>

                <label className={fieldClass(errors, "customerEmail")}>
                  <span>Email address</span>
                  <input
                    type="email"
                    name="customerEmail"
                    value={details.customerEmail}
                    onChange={updateDetails}
                    onBlur={validateOnBlur}
                    autoComplete="email"
                    maxLength="254"
                    aria-invalid={Boolean(errors.customerEmail)}
                    aria-describedby={
                      errors.customerEmail ? "customerEmail-error" : undefined
                    }
                    required
                  />
                  <FieldError errors={errors} name="customerEmail" />
                </label>

                <label className={fieldClass(errors, "customerPhone")}>
                  <span>Phone number</span>
                  <div className="purchase-phone-input">
                    <span aria-hidden="true">+91</span>
                    <input
                      type="tel"
                      name="customerPhone"
                      value={details.customerPhone}
                      onChange={updateDetails}
                      onBlur={validateOnBlur}
                      autoComplete="tel-national"
                      inputMode="numeric"
                      maxLength="10"
                      placeholder="98765 43210"
                      aria-label="Indian mobile number"
                      aria-invalid={Boolean(errors.customerPhone)}
                      aria-describedby={
                        errors.customerPhone ? "customerPhone-error" : undefined
                      }
                      required
                    />
                  </div>
                  <FieldError errors={errors} name="customerPhone" />
                </label>
              </div>
            </fieldset>

            <fieldset>
              <legend>Shipping address</legend>
              <div className="purchase-field-grid">
                <label className={fieldClass(errors, "addressLine1", true)}>
                  <span>Address line 1</span>
                  <input
                    type="text"
                    name="addressLine1"
                    value={details.addressLine1}
                    onChange={updateDetails}
                    onBlur={validateOnBlur}
                    autoComplete="address-line1"
                    maxLength="250"
                    aria-invalid={Boolean(errors.addressLine1)}
                    aria-describedby={
                      errors.addressLine1 ? "addressLine1-error" : undefined
                    }
                    required
                  />
                  <FieldError errors={errors} name="addressLine1" />
                </label>

                <label className="purchase-field is-wide">
                  <span>
                    Address line 2 <small>Optional</small>
                  </span>
                  <input
                    type="text"
                    name="addressLine2"
                    value={details.addressLine2}
                    onChange={updateDetails}
                    autoComplete="address-line2"
                    maxLength="250"
                  />
                  <FieldError errors={errors} name="addressLine2" />
                </label>

                <label className={fieldClass(errors, "city")}>
                  <span>City</span>
                  <input
                    type="text"
                    name="city"
                    value={details.city}
                    onChange={updateDetails}
                    onBlur={validateOnBlur}
                    autoComplete="address-level2"
                    maxLength="100"
                    aria-invalid={Boolean(errors.city)}
                    aria-describedby={errors.city ? "city-error" : undefined}
                    required
                  />
                  <FieldError errors={errors} name="city" />
                </label>

                <label className={fieldClass(errors, "state")}>
                  <span>State</span>
                  <input
                    type="text"
                    name="state"
                    value={details.state}
                    onChange={updateDetails}
                    onBlur={validateOnBlur}
                    autoComplete="address-level1"
                    maxLength="100"
                    aria-invalid={Boolean(errors.state)}
                    aria-describedby={errors.state ? "state-error" : undefined}
                    required
                  />
                  <FieldError errors={errors} name="state" />
                </label>

                <label className={fieldClass(errors, "postalCode")}>
                  <span>Postal code</span>
                  <input
                    type="text"
                    name="postalCode"
                    value={details.postalCode}
                    onChange={updateDetails}
                    onBlur={validateOnBlur}
                    autoComplete="postal-code"
                    inputMode="numeric"
                    maxLength="6"
                    aria-invalid={Boolean(errors.postalCode)}
                    aria-describedby={
                      errors.postalCode ? "postalCode-error" : undefined
                    }
                    required
                  />
                  <FieldError errors={errors} name="postalCode" />
                </label>

                <label className="purchase-field">
                  <span>Country</span>
                  <input
                    type="text"
                    name="country"
                    value={details.country}
                    autoComplete="country-name"
                    readOnly
                  />
                  <FieldError errors={errors} name="country" />
                </label>
              </div>
            </fieldset>
          </div>

          <aside
            className="purchase-product"
            aria-label="Order quantity and total"
          >
            <div className="purchase-book-stage">
              <img
                src="/media/images/Oath_book_3d.avif"
                alt="Three-dimensional view of The Oath of the Unhuan hardcover book"
              />
            </div>

            <div className="purchase-quantity">
              <p>Book count</p>
              <div className="purchase-stepper">
                <button
                  type="button"
                  onClick={() => adjustQuantity(-1)}
                  disabled={
                    quantity === MIN_QUANTITY || checkoutBusy || discountBusy
                  }
                  aria-label="Decrease book count"
                >
                  <Minus aria-hidden="true" />
                </button>
                <output aria-live="polite" aria-label={`${quantity} books`}>
                  {quantity}
                </output>
                <button
                  type="button"
                  onClick={() => adjustQuantity(1)}
                  disabled={
                    quantity === MAX_QUANTITY || checkoutBusy || discountBusy
                  }
                  aria-label="Increase book count"
                >
                  <Plus aria-hidden="true" />
                </button>
              </div>
            </div>

            <div className="purchase-discount">
              <label htmlFor="discount-code">Discount code</label>
              <div
                className={`purchase-discount-control${
                  discountPhase === "error" ? " has-error" : ""
                }`}
              >
                <input
                  id="discount-code"
                  type="text"
                  value={discountInput}
                  onChange={updateDiscountInput}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" && !appliedDiscount) {
                      event.preventDefault();
                      applyDiscount();
                    }
                  }}
                  placeholder="Enter code"
                  autoComplete="off"
                  maxLength="32"
                  readOnly={Boolean(appliedDiscount)}
                  disabled={checkoutBusy}
                  aria-invalid={discountPhase === "error"}
                  aria-describedby="discount-code-status"
                />
                <button
                  type="button"
                  onClick={appliedDiscount ? removeDiscount : applyDiscount}
                  disabled={checkoutBusy || discountBusy}
                >
                  {discountBusy
                    ? "Checking…"
                    : appliedDiscount
                      ? "Remove"
                      : "Apply"}
                </button>
              </div>
              <p
                id="discount-code-status"
                className={`purchase-discount-message${
                  discountPhase === "applied" ? " is-success" : ""
                }`}
                role={discountPhase === "error" ? "alert" : "status"}
              >
                {discountMessage || "\u00a0"}
              </p>
            </div>

            <div className="purchase-total" aria-live="polite">
              <div>
                <span>
                  {quantity} × {formatPaise(UNIT_PRICE_PAISE)}
                </span>
                <strong>{formatPaise(bookSubtotalPaise)}</strong>
              </div>
              {appliedDiscount && (
                <div className="is-discount">
                  <span>Discount ({appliedDiscount.code})</span>
                  <strong>−{formatPaise(discountTotalPaise)}</strong>
                </div>
              )}
              <div>
                <span>Handling &amp; shipping</span>
                <strong>{formatPaise(SHIPPING_PAISE)}</strong>
              </div>
              <div className="is-total">
                <span>Total</span>
                <strong>{formatPaise(totalPaise)}</strong>
              </div>
            </div>

            <SpecularButton
              className="purchase-buy-button"
              type="submit"
              disabled={
                checkoutBusy || discountBusy || checkoutPhase === "success"
              }
              ariaBusy={checkoutBusy}
            >
              Buy Now
            </SpecularButton>

            {checkoutMessage && (
              <p
                className={`purchase-checkout-message${
                  checkoutPhase === "success" ? " is-success" : ""
                }`}
                role={checkoutPhase === "success" ? "status" : "alert"}
              >
                {checkoutMessage}
              </p>
            )}
          </aside>
        </form>

        <blockquote className="purchase-author-note">
          <p>
            “I want to thank you for purchasing this book and supporting me. I
            am excited for you to step into the world that I have created and I
            hope that it will captivate you and help you look inward, just like
            it did to me.”
          </p>
          <cite>— Mahesh Thakur</cite>
        </blockquote>
      </section>
    </main>
  );
}
